import { useState } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { useUserStore } from '@/stores/userStore'
import { useWorkoutStore } from '@/stores/workoutStore'
import { useBodyWeightStore } from '@/stores/bodyWeightStore'
import { useCycleStore } from '@/stores/cycleStore'

function ExportDataModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
}) {
  const { sessions } = useWorkoutStore()
  const { logs: weightLogs } = useBodyWeightStore()
  const { cycles, injections } = useCycleStore()
  const [exporting, setExporting] = useState(false)

  if (!isOpen) return null

  const handleExportJSON = async () => {
    setExporting(true)

    const data = {
      exportedAt: new Date().toISOString(),
      workouts: sessions,
      bodyWeight: weightLogs,
      cycles,
      injections,
    }

    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `xframe-export-${new Date().toISOString().split('T')[0]}.json`
    a.click()
    URL.revokeObjectURL(url)

    setExporting(false)
    onClose()
  }

  const handleExportCSV = async () => {
    setExporting(true)

    // Export workouts as CSV
    const workoutCsv = [
      'Date,Type,Phase,Week,Volume (kg),Duration (min)',
      ...sessions.map(
        (s) =>
          `${s.date},${s.workout_type},${s.phase},${s.week},${s.total_volume_kg || 0},${s.duration_minutes || 0}`
      ),
    ].join('\n')

    const blob = new Blob([workoutCsv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `xframe-workouts-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)

    setExporting(false)
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-surface rounded-xl w-full max-w-md">
        <div className="p-4 border-b border-border">
          <h2 className="font-semibold text-text-primary">Export Data</h2>
        </div>

        <div className="p-4 space-y-4">
          <p className="text-sm text-text-secondary">
            Export your workout history, body weight logs, and cycle data.
          </p>

          <div className="space-y-3">
            <button
              onClick={handleExportJSON}
              disabled={exporting}
              className="w-full p-3 bg-elevated hover:bg-surface rounded-lg text-left transition-colors"
            >
              <p className="font-medium text-text-primary">Export as JSON</p>
              <p className="text-xs text-text-muted">
                Full export including all data (recommended for backup)
              </p>
            </button>

            <button
              onClick={handleExportCSV}
              disabled={exporting}
              className="w-full p-3 bg-elevated hover:bg-surface rounded-lg text-left transition-colors"
            >
              <p className="font-medium text-text-primary">Export Workouts as CSV</p>
              <p className="text-xs text-text-muted">
                Spreadsheet-compatible workout history
              </p>
            </button>
          </div>

          <div className="text-xs text-text-muted">
            <p>Summary:</p>
            <ul className="mt-1 space-y-1 ml-4 list-disc">
              <li>{sessions.length} workout sessions</li>
              <li>{weightLogs.length} body weight logs</li>
              <li>{cycles.length} cycles</li>
              <li>{injections.length} injection logs</li>
            </ul>
          </div>
        </div>

        <div className="p-4 border-t border-border">
          <button
            onClick={onClose}
            className="w-full py-2 bg-elevated text-text-secondary rounded-lg font-medium"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

function TRTDoseInput() {
  const { profile, updateProfile } = useUserStore()
  const [dose, setDose] = useState(profile?.trt_dose_mg?.toString() || '')

  const handleSave = async () => {
    const parsed = parseInt(dose)
    if (!isNaN(parsed) && parsed > 0) {
      await updateProfile({ trt_dose_mg: parsed })
    }
  }

  return (
    <div>
      <label className="label">TRT Dose (mg/week)</label>
      <div className="flex gap-2">
        <input
          type="number"
          value={dose}
          onChange={(e) => setDose(e.target.value)}
          placeholder="e.g., 150"
          className="flex-1 p-2 bg-elevated border border-border rounded-lg text-text-primary"
        />
        <button
          onClick={handleSave}
          disabled={!dose || parseInt(dose) === profile?.trt_dose_mg}
          className="px-4 py-2 bg-primary text-white rounded-lg font-medium disabled:opacity-50"
        >
          Save
        </button>
      </div>
      <p className="text-xs text-text-muted mt-1">
        Your cruise/TRT dose for reference during cycles
      </p>
    </div>
  )
}

function DisplayNameInput() {
  const { profile, updateProfile } = useUserStore()
  const [name, setName] = useState(profile?.display_name || '')

  const handleSave = async () => {
    if (name.trim()) {
      await updateProfile({ display_name: name.trim() })
    }
  }

  return (
    <div>
      <label className="label">Display Name</label>
      <div className="flex gap-2">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter your name"
          className="flex-1 p-2 bg-elevated border border-border rounded-lg text-text-primary"
        />
        <button
          onClick={handleSave}
          disabled={!name.trim() || name.trim() === profile?.display_name}
          className="px-4 py-2 bg-primary text-white rounded-lg font-medium disabled:opacity-50"
        >
          Save
        </button>
      </div>
    </div>
  )
}

export default function Settings() {
  const { signOut, user } = useAuthStore()
  const { profile, setUnitSystem, setVolumeTier, setPhase } = useUserStore()
  const [showExportModal, setShowExportModal] = useState(false)

  const handleSignOut = async () => {
    if (confirm('Are you sure you want to sign out?')) {
      await signOut()
    }
  }

  return (
    <div className="p-4 space-y-4 pb-24">
      {/* Account Section */}
      <div className="card">
        <h2 className="text-lg font-semibold text-text-primary mb-4">Account</h2>

        <div className="space-y-4">
          <DisplayNameInput />

          <div className="flex items-center justify-between py-2 border-t border-border pt-4">
            <span className="text-text-secondary">Email</span>
            <span className="text-text-primary text-sm">{user?.email}</span>
          </div>

          <div className="border-t border-border pt-3">
            <button onClick={handleSignOut} className="btn-danger w-full">
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Program Settings */}
      <div className="card">
        <h2 className="text-lg font-semibold text-text-primary mb-4">Program</h2>

        <div className="space-y-4">
          {/* Phase */}
          <div>
            <label className="label">Current Phase</label>
            <div className="grid grid-cols-2 gap-2">
              {([1, 2] as const).map((phase) => (
                <button
                  key={phase}
                  onClick={() => setPhase(phase)}
                  className={`py-2 px-4 rounded-lg font-medium transition-colors ${
                    profile?.current_phase === phase
                      ? 'bg-primary text-white'
                      : 'bg-elevated text-text-secondary hover:text-text-primary'
                  }`}
                >
                  Phase {phase}
                </button>
              ))}
            </div>
            <p className="text-xs text-text-muted mt-2">
              {profile?.current_phase === 1
                ? 'Phase 1: Initial hypertrophy focus'
                : 'Phase 2: Advanced progression with intensifiers'}
            </p>
          </div>

          {/* Volume Tier */}
          <div>
            <label className="label">Volume Tier</label>
            <div className="grid grid-cols-3 gap-2">
              {([1, 2, 3] as const).map((tier) => (
                <button
                  key={tier}
                  onClick={() => setVolumeTier(tier)}
                  className={`py-2 px-4 rounded-lg font-medium transition-colors ${
                    profile?.volume_tier === tier
                      ? 'bg-primary text-white'
                      : 'bg-elevated text-text-secondary hover:text-text-primary'
                  }`}
                >
                  Tier {tier}
                </button>
              ))}
            </div>
            <p className="text-xs text-text-muted mt-2">
              {profile?.volume_tier === 1
                ? 'Tier 1: 4-5 days/week (Beginners)'
                : profile?.volume_tier === 2
                ? 'Tier 2: 5-6 days/week (Intermediate)'
                : 'Tier 3: 6 days/week (Advanced)'}
            </p>
          </div>

          {/* Current Week */}
          <div className="border-t border-border pt-4">
            <div className="flex items-center justify-between">
              <span className="text-text-secondary">Current Week</span>
              <span className="text-text-primary font-medium">
                Week {profile?.current_week || 1}
                {profile?.current_week === 8 && (
                  <span className="text-success ml-2">(Deload)</span>
                )}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Units */}
      <div className="card">
        <h2 className="text-lg font-semibold text-text-primary mb-4">Units</h2>

        <div>
          <label className="label">Weight Unit</label>
          <div className="grid grid-cols-2 gap-2">
            {(['imperial', 'metric'] as const).map((unit) => (
              <button
                key={unit}
                onClick={() => setUnitSystem(unit)}
                className={`py-2 px-4 rounded-lg font-medium transition-colors ${
                  profile?.unit_system === unit
                    ? 'bg-primary text-white'
                    : 'bg-elevated text-text-secondary hover:text-text-primary'
                }`}
              >
                {unit === 'imperial' ? 'Pounds (lbs)' : 'Kilograms (kg)'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* TRT Settings */}
      <div className="card">
        <h2 className="text-lg font-semibold text-text-primary mb-4">TRT/Cruise</h2>
        <TRTDoseInput />
      </div>

      {/* Data Export */}
      <div className="card">
        <h2 className="text-lg font-semibold text-text-primary mb-4">Data</h2>

        <div className="space-y-3">
          <button
            onClick={() => setShowExportModal(true)}
            className="btn-secondary w-full"
          >
            Export Data
          </button>
          <p className="text-xs text-text-muted text-center">
            Export your workouts, body weight, and cycle data as CSV or JSON.
          </p>
        </div>
      </div>

      {/* App Info */}
      <div className="card">
        <h2 className="text-lg font-semibold text-text-primary mb-4">About</h2>

        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-text-muted">Version</span>
            <span className="text-text-secondary">0.2.0 (Phase 2)</span>
          </div>
          <div className="flex justify-between">
            <span className="text-text-muted">Program</span>
            <span className="text-text-secondary">J3U X-Frame Hypertrophy</span>
          </div>
          <div className="flex justify-between">
            <span className="text-text-muted">Status</span>
            <span className="text-success">Online</span>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-border">
          <p className="text-xs text-text-muted text-center">
            Built with React + Vite + Supabase + Dexie
          </p>
        </div>
      </div>

      <ExportDataModal isOpen={showExportModal} onClose={() => setShowExportModal(false)} />
    </div>
  )
}
